import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { specialistService } from '@/services/api'

interface ConversationItem {
  id: number
  user: {
    id: number
    email: string
    first_name: string
    last_name: string
  }
  status: string
  created_at: string
  updated_at: string
  last_message_at: string | null
  unread_count: number
  latest_message: {
    content: string
    sender_type: string
    created_at: string
  } | null
}

export default function SpecialistDashboardPage() {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [conversations, setConversations] = useState<ConversationItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState<'all' | 'transferred' | 'closed'>('all')
  const [showUnreadOnly, setShowUnreadOnly] = useState(false)

  // Fetch conversations
  useEffect(() => {
    loadConversations()
  }, [filter, showUnreadOnly])

  const loadConversations = async () => {
    setLoading(true)
    setError('')

    try {
      const filters: any = {}
      if (filter !== 'all') {
        filters.status = filter
      }
      if (showUnreadOnly) {
        filters.unread = true
      }

      const data = await specialistService.getConversations(filters)
      setConversations(data.results || [])
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load conversations')
    } finally {
      setLoading(false)
    }
  }

  // Takeover handler for future use
  // const handleTakeover = async (conversationId: number) => {
  //   try {
  //     await specialistService.takeoverConversation(conversationId)
  //     navigate(`/specialist/chat/${conversationId}`)
  //   } catch (err: any) {
  //     setError(err.response?.data?.detail || 'Failed to take over conversation')
  //   }
  // }

  const handleComplete = async (conversationId: number) => {
    if (!confirm('Are you sure you want to mark this conversation as complete?')) {
      return
    }

    try {
      await specialistService.completeConversation(conversationId)
      // Reload conversations
      loadConversations()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to complete conversation')
    }
  }

  const handleViewConversation = (conversationId: number) => {
    navigate(`/specialist/chat/${conversationId}`)
  }

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'Never'
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return date.toLocaleDateString()
  }

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading conversations...</p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <div style={{ padding: '1.5rem', backgroundColor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Specialist Dashboard</h1>
            <p style={{ margin: '0.25rem 0 0 0', color: '#666', fontSize: '0.875rem' }}>
              Welcome, Dr. {user?.last_name || 'Specialist'}
            </p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ padding: '1rem', backgroundColor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <div>
            <label style={{ marginRight: '0.5rem', fontWeight: 'bold' }}>Filter:</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            >
              <option value="all">All Conversations</option>
              <option value="transferred">Active</option>
              <option value="closed">Completed</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showUnreadOnly}
                onChange={(e) => setShowUnreadOnly(e.target.checked)}
              />
              Unread only
            </label>
          </div>
          <div style={{ marginLeft: 'auto', color: '#666', fontSize: '0.875rem' }}>
            {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: '1rem', backgroundColor: '#ffebee', color: '#c62828' }}>
          {error}
        </div>
      )}

      {/* Conversations List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
        {conversations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            <p style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>No conversations found</p>
            <p style={{ fontSize: '0.875rem' }}>
              {showUnreadOnly ? 'No unread conversations' : 'You have no assigned conversations yet'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {conversations.map((conversation) => (
              <div
                key={conversation.id}
                style={{
                  padding: '1rem',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)')}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
                onClick={() => handleViewConversation(conversation.id)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <h3 style={{ margin: 0, fontSize: '1.125rem' }}>
                        {conversation.user.first_name} {conversation.user.last_name}
                      </h3>
                      {conversation.unread_count > 0 && (
                        <span
                          style={{
                            backgroundColor: '#2196f3',
                            color: 'white',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                          }}
                        >
                          {conversation.unread_count} unread
                        </span>
                      )}
                      <span
                        style={{
                          padding: '0.125rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          backgroundColor:
                            conversation.status === 'transferred'
                              ? '#e3f2fd'
                              : conversation.status === 'closed'
                              ? '#f5f5f5'
                              : '#fff3e0',
                          color:
                            conversation.status === 'transferred'
                              ? '#1976d2'
                              : conversation.status === 'closed'
                              ? '#666'
                              : '#f57c00',
                        }}
                      >
                        {conversation.status}
                      </span>
                    </div>
                    <p style={{ margin: '0.5rem 0', color: '#666', fontSize: '0.875rem' }}>
                      {conversation.user.email}
                    </p>
                    {conversation.latest_message && (
                      <p
                        style={{
                          margin: '0.5rem 0 0 0',
                          color: '#888',
                          fontSize: '0.875rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <strong>
                          {conversation.latest_message.sender_type === 'user'
                            ? 'User'
                            : conversation.latest_message.sender_type === 'specialist'
                            ? 'You'
                            : 'AI'}
                          :
                        </strong>{' '}
                        {conversation.latest_message.content}
                      </p>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', marginLeft: '1rem' }}>
                    <p style={{ margin: '0 0 0.5rem 0', color: '#888', fontSize: '0.75rem' }}>
                      {formatTimestamp(conversation.last_message_at)}
                    </p>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {conversation.status === 'transferred' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleComplete(conversation.id)
                          }}
                          style={{
                            padding: '0.375rem 0.75rem',
                            backgroundColor: '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                          }}
                        >
                          Complete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
